import { Controller } from "@hotwired/stimulus"

// Per-column filter popover (rendered by Cup::DataTable::FilterPopover) —
// spec: docs/specs/overviews/_settings.md (the per-column filters section).
//
// - A trigger button toggles the panel; it closes on outside-click and Esc.
// - Text columns: the operator <select> rewrites the value <input>'s `name` to a
//   canonical Ransack predicate (q[<attr>_<predicate>]) and hides the value for
//   the value-less blank/present predicates ("is blank" / "is not blank").
// - Cancel restores the last-applied operator + value and closes.
// - Select columns (roles) carry no operator/value targets; the controller still
//   provides toggle + outside-click/Esc close.
// - Multi columns embed a token-select (chips); it applies on the button like the
//   text branch, so Cancel restores the chips that were applied on open.
//
// The rest of the list context this column's GET form would otherwise drop
// (other columns, the toolbar search, sort, the upload an embedded list is
// scoped to) is rendered server-side by Cup::DataTable::ListContext, from the
// request that rendered the popover. Reading it off window.location here is what
// lost the upload scope inside the upload-detail embed, whose address is the
// upload's, not the list's.
export default class extends Controller {
  static targets = ["panel", "operator", "value", "valueWrap", "rangeValue"]
  static outlets = ["token-select"]
  static values = { attribute: String }

  connect() {
    if (this.hasOperatorTarget) {
      this.initialOperator = this.operatorTarget.value
      this.update()
    }
    if (this.hasValueTarget) this.initialValue = this.valueTarget.value
    // Date-range columns carry two date inputs instead of one operator/value
    // pair; remember both so Cancel can restore them.
    this.initialRangeValues = this.rangeValueTargets.map((t) => t.value)
    // Read the chips straight from the DOM rather than through the outlet: the
    // outlet may not have connected yet, but the server-rendered hidden inputs
    // are already the last-applied selection.
    this.initialTokens = Array.from(
      this.element.querySelectorAll("[data-token-select-target='chip'] input[type='hidden']:not([disabled])"),
      (input) => input.value
    )
    this.onDocPointerDown = this.onDocPointerDown.bind(this)
    this.onDocClick = this.onDocClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.removeGlobalListeners()
  }

  toggle(event) {
    event.preventDefault()
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    this.suspendHeaderDrag()
    document.addEventListener("pointerdown", this.onDocPointerDown)
    document.addEventListener("click", this.onDocClick)
    document.addEventListener("keydown", this.onKeydown)
    // A multi column opens on its chip box alone: focusing it here would drop the
    // option list open before the user has asked for it, and the popover should
    // read as one clean input, like the text filter's.
    if (this.hasTokenSelectOutlet) return

    const field = this.hasValueTarget && !this.valueTarget.classList.contains("hidden")
      ? this.valueTarget
      : (this.hasOperatorTarget ? this.operatorTarget : this.rangeValueTargets[0] || null)
    field?.focus()
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.resumeHeaderDrag()
    this.removeGlobalListeners()
  }

  // The panel lives inside the column's draggable <th> (columns_controller marks
  // every movable header draggable). While the popover is open the user is
  // filtering — selecting text in the value input — not reordering, and a
  // draggable ancestor makes the browser hijack that text-selection drag as a
  // column reorder. Preventing the reorder in dragstart isn't enough: the browser
  // has already claimed the gesture as a drag, so text never selects. Keep the
  // header non-draggable for as long as the popover is open; restore on close.
  suspendHeaderDrag() {
    this.draggableHeader = this.element.closest("th[draggable='true']")
    if (this.draggableHeader) this.draggableHeader.draggable = false
  }

  resumeHeaderDrag() {
    if (this.draggableHeader) this.draggableHeader.draggable = true
    this.draggableHeader = null
  }

  // Cancel: discard any pending (not-yet-applied) edits, then close.
  cancel() {
    if (this.hasOperatorTarget) {
      this.operatorTarget.value = this.initialOperator
      this.update()
    }
    if (this.hasValueTarget) this.valueTarget.value = this.initialValue
    this.rangeValueTargets.forEach((t, i) => (t.value = this.initialRangeValues[i]))
    if (this.hasTokenSelectOutlet) this.tokenSelectOutlet.setValues(this.initialTokens)
    this.close()
  }

  // Stop clicks inside the panel from bubbling to the document close-handler.
  stop(event) {
    event.stopPropagation()
  }

  // Select-type filters (e.g. roles) auto-apply: a <select> change can't submit
  // a form natively, so request it. Text filters submit natively (Enter / the
  // apply button).
  submit(event) {
    event.target.closest("form")?.requestSubmit()
  }

  isOpen() {
    return !this.panelTarget.classList.contains("hidden")
  }

  // Remember where the gesture began so a text-selection drag that starts inside
  // the value input but is released outside doesn't close us. Such a drag fires a
  // click targeted at the common ancestor of press+release — outside the popover —
  // which onDocClick would otherwise read as an outside-click. Only a click whose
  // press also landed outside is a genuine dismiss.
  onDocPointerDown(event) {
    this.pointerDownInside = this.element.contains(event.target)
  }

  onDocClick(event) {
    if (this.pointerDownInside) return
    if (!this.element.contains(event.target)) this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  removeGlobalListeners() {
    document.removeEventListener("pointerdown", this.onDocPointerDown)
    document.removeEventListener("click", this.onDocClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  update() {
    const predicate = this.operatorTarget.value
    const valueless = predicate === "blank" || predicate === "present"

    this.valueTarget.name = `q[${this.attributeValue}_${predicate}]`

    const wrap = this.hasValueWrapTarget ? this.valueWrapTarget : this.valueTarget
    if (valueless) {
      this.valueTarget.value = "true"
      wrap.classList.add("hidden")
    } else {
      wrap.classList.remove("hidden")
      // Don't carry the sentinel "true" forward into a textual predicate.
      if (this.valueTarget.value === "true") this.valueTarget.value = ""
    }
  }
}
